import { DocumentClassificationStatus } from "documents/classification/document-classification-status";
import { DocumentContentAccess } from "documents/classification/document-content-access";
import type { DownloadVerdictFields } from "documents/downloads/document-download-verdict";
import { DocumentFileScanStatus } from "documents/downloads/document-file-scan-status";
import { DocumentSubjectMatter } from "permissions/resources/documents/document-subject-matter";

/**
 * Why the server will refuse this reader's download of this document, or undefined when it will not
 * refuse it for any reason the page can know in advance (#6992).
 *
 * The server's refusals, in the order it checks them: the malware scan (`file_scan_pending`,
 * `file_infected`, `file_scan_failed`), then quarantine (`document_still_processing`,
 * `document_processing_failed`), then subject matter (`document_subject_matter_restricted`). See
 * `DocumentQueriesController.DownloadDocumentByIdAsync`. This says the same thing before the click,
 * so a Download control can be disabled instead of answering with a snackbar after it.
 *
 * It decides on what the documents API said for this reader, and on nothing else. A field the
 * payload did not carry - an older API, a work or proposal projection, an upload's answer - is no
 * evidence of a refusal, so the control stays enabled and the server still decides.
 */
export function documentDownloadWithheldReason(document: DownloadVerdictFields | undefined): string | undefined {
  if (!document) return undefined;

  switch (document.scanStatus) {
    case DocumentFileScanStatus.Pending:
      return "Security check still running. Try again in a minute.";
    case DocumentFileScanStatus.Infected:
      return "Threat found. This file cannot be downloaded.";
    case DocumentFileScanStatus.Failed:
      return "Security check failed. Upload the file again.";
    default:
      break;
  }

  if (document.contentAccess === DocumentContentAccess.Quarantined) {
    return document.classificationStatus === DocumentClassificationStatus.Failed
      ? "Processing failed. Upload the document again."
      : "Still processing. Try again in a minute.";
  }

  if (document.contentAccess === DocumentContentAccess.SubjectMatterRestricted) {
    return subjectMatterRefusal(document.restrictedSubjectMatters);
  }

  return undefined;
}

/**
 * "You don't have access to Financial documents", naming the labels the reader lacks the way a
 * refused download names them. An API that did not name them still gets a true sentence.
 */
export function subjectMatterRefusal(restricted: DocumentSubjectMatter[] | undefined): string {
  if (!restricted?.length) return "You don't have access to this document's subject matter.";
  const labels = restricted.map((subjectMatter) => DocumentSubjectMatter[subjectMatter]).join(" and ");
  return `You don't have access to ${labels} documents.`;
}
